<template>
  <v-app id="inspire">
    <v-app-bar flat color="primary">
      <v-img
        alt="DevCentre logo"
        :src="logo_url"
        class="mr-5"
        background-color="primary"
        max-width="60"
      />

      <h2>{{ title }}</h2>
      <v-chip
        v-if="currentUser && currentUser.tenant_name"
        size="small"
        variant="tonal"
        class="ml-4"
      >
        {{ currentUser.tenant_name }}
      </v-chip>

      <v-spacer></v-spacer>
      <v-btn
        v-for="link in filteredLinks"
        :key="link"
        :text="link.label"
        :to="link.uri"
        class="hidden-sm-and-down"
        >{{ link.label }}</v-btn
      >
      <v-btn v-if="currentUser" to="/todo" class="hidden-sm-and-down">
        To Do
        <v-badge v-if="openTaskCount" :content="openTaskCount" color="error" inline class="ml-1" />
      </v-btn>
      <v-btn
        v-if="isFreeTier"
        to="/subscription-plans"
        color="amber"
        variant="elevated"
        class="hidden-sm-and-down ml-2"
      >
        Subscribe Now
      </v-btn>

      <!-- Add user menu -->
      <v-menu v-if="currentUser">
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <v-avatar color="surface-variant">
              {{ currentUser.name.charAt(0).toUpperCase() }}
            </v-avatar>
          </v-btn>
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-title>{{ currentUser.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ currentUser.email }}</v-list-item-subtitle>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item
            v-if="currentTenant && currentRole === 'admin'"
            :to="`/tenants/${currentTenant.tenant_id}/settings`"
            link
          >
            <v-list-item-title>Settings</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="currentUser" to="/tenants" link>
            <v-list-item-title>Switch Tenant</v-list-item-title>
          </v-list-item>
          <v-list-item @click="signOut">
            <v-list-item-title>Sign Out</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Mobile hamburger menu -->
      <v-app-bar-nav-icon class="hidden-md-and-up" @click="drawer = !drawer"></v-app-bar-nav-icon>
    </v-app-bar>

    <!-- Mobile navigation drawer -->
    <!-- Add sign in to mobile drawer if not logged in -->
    <v-navigation-drawer v-model="drawer" app temporary>
      <v-list>
        <v-list-item v-if="currentUser">
          <v-list-item-title>{{ currentUser.name }}</v-list-item-title>
          <v-list-item-subtitle>{{ currentUser.email }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-else to="/login" link>
          <v-list-item-title>Sign In</v-list-item-title>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item v-for="link in links" :key="link.uri" :to="link.uri" link>
          <v-list-item-title>{{ link.label }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="currentUser" to="/todo" link>
          <v-list-item-title>
            To Do
            <v-badge
              v-if="openTaskCount"
              :content="openTaskCount"
              color="error"
              inline
              class="ml-1"
            />
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="currentUser && currentTenant && currentRole === 'admin'"
          :to="`/tenants/${currentTenant.tenant_id}/settings`"
          link
        >
          <v-list-item-title>Settings</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isFreeTier" to="/subscription-plans" link>
          <v-list-item-title>Subscribe Now</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="currentUser" to="/tenants" link>
          <v-list-item-title>Switch Tenant</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="currentUser" @click="signOut" link>
          <v-list-item-title>Sign Out</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-main class="bg-grey-lighten-3" fill-height>
      <!-- Use the v-container directly inside v-main with the "fill-height" property -->
      <v-container fill-height class="align-center justify-center" fluid>
        <!-- Wrap the content in a flex row that centers items vertically and horizontally -->
        <v-row align="center" justify="center">
          <v-col cols="12">
            <!-- Adjusted for centering -->
            <router-view />
          </v-col>
        </v-row>
      </v-container>
    </v-main>
    <v-footer color="#2ab3b8" style="max-height: 60px" padless>
      <v-container fill-height class="align-center justify-center" fluid>
        <v-btn text="About" variant="text" to="/about"></v-btn>
        <v-btn text="Contact" variant="text" to="/contact"></v-btn>
        <v-btn text="Terms of Service" variant="text" to="/terms"></v-btn>
        <v-btn text="Privacy Policy" variant="text" to="/privacy"></v-btn>
      </v-container>
    </v-footer>
  </v-app>
</template>

<script>
import logoImage from "@/assets/devcentrelogo.png";
import AuthService from "@/services/AuthService";
import APIService from "@/services/APIService";
import eventBus from "@/plugins/eventBus";

export default {
  name: "App",
  components: {},
  data: () => ({
    title: "DevCentre Workflow Automation",
    drawer: false,
    logo_url: logoImage,
    links: [
      { label: "Processes", uri: "/kanban", public: false },
      { label: "Tables", uri: "/kanban/tables", public: false },
      { label: "Views", uri: "/kanban/views", public: false },
    ],
    openTaskCount: 0,
    dev: process.env.NODE_ENV === "development",
  }),
  async created() {
    await this.checkUser();
    this.refreshTaskCount();
    eventBus.on("user-signed-in", this.checkUser);
    eventBus.on("user-signed-in", this.refreshTaskCount);
    eventBus.on("tasks-updated", this.refreshTaskCount);
  },
  watch: {
    $route() {
      this.refreshTaskCount();
    },
  },
  methods: {
    async checkUser() {
      await this.$store.dispatch("tenancy/fetchUser");
    },
    async refreshTaskCount() {
      if (!AuthService.isAuthenticated() || !APIService.getCurrentTenantId()) {
        this.openTaskCount = 0;
        return;
      }
      try {
        const res = await APIService.getMyTasks();
        this.openTaskCount = res.data.count || 0;
      } catch {
        this.openTaskCount = 0;
      }
    },
    async signOut() {
      try {
        await AuthService.signOut();
        this.$store.commit("tenancy/SET_USER", null);
        this.$router.push("/about");
      } catch (error) {
        console.error("Error signing out:", error);
      }
    },
  },
  computed: {
    filteredLinks() {
      return this.links.filter((link) => link.public || this.currentUser);
    },
    currentUser() {
      return this.$store.getters["tenancy/currentUser"];
    },
    currentTenant() {
      return this.$store.getters["tenancy/currentTenant"];
    },
    currentRole() {
      return this.$store.getters.currentRole;
    },
    isFreeTier() {
      return (
        this.currentUser && this.currentTenant && this.$store.getters.tenantTier === "freemium"
      );
    },
  },
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: rgb(0, 10, 69);
}

#nav-bar {
  background-color: #2ab3b8;
  color: white;
  padding: 1rem;
}

nav {
  padding: 1rem;
  background-color: #2ab3b8;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}

.v-text-field.solo-inverted {
  background-color: #333;
  border-radius: 4px;
  color: white;
}

.v-text-field.solo-inverted::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.v-text-field .v-icon {
  color: white;
}

.v-carousel-item {
  position: relative;
}

.devcentre-title {
  text-decoration: none;
  color: white;
}

/* Replaced all media query-based sizing with clamp() */
.devcentre-intro {
  font-size: clamp(1.5rem, 2.5vw, 3rem);
  font-weight: 700;
  line-height: 1.2;
  background: linear-gradient(45deg, #2ab3b8, #1e7b80);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.devcentre-subintro {
  font-size: clamp(1rem, 1.5vw, 2rem);
  line-height: 1.6;
  color: #37474f;
}

.devcentre-text {
  font-size: 1vw;
  text-align: left;
  color: #0a1a1b;
}

.rounded-image {
  border-radius: 20px;
}

.image-header-span {
  background-color: rgba(18, 72, 87, 0.5);
  color: white;
  font-weight: bold;
  margin-right: 10;
  padding: 1% 1% 1% 1%;
  float: right;
}

/* Gold subscription styling */
.gold-subscription {
  color: #818302;
  background-color: rgba(255, 215, 0, 0.1) !important;
}

.gold-subscription:hover {
  background-color: rgba(255, 215, 0, 0.2) !important;
}
</style>
